import React, { useEffect, useMemo, useState } from 'react';
import { PageFrame, PageHeader, FilterBar, FilterMultiSelect, Button } from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import { Tooltip } from '@mui/material';
import {
    SelectOption
} from '@/types/client';
import { schemesApi, supportingDataApi, sectorWeightAPI } from '@/services/api';
import { useHoldingsDateRestriction } from '@/hooks/useHoldingsDateRestriction';
import { LastWorkingDateFilter } from '@/components/utils/filters';
import SectorWeightComparison from './SectorWeightComparison';


const DEFAULT_ALL: SelectOption[] = Object.freeze([{ value: 'all', label: 'All' }]) as SelectOption[];
const DEFAULT_BENCHMARK: SelectOption[] = Object.freeze([{ value: 'BSE500', label: 'BSE 500' }]) as SelectOption[];
const DEFAULT_STRATEGY_NAME = 'Core Domestic';

const SectorWeightPage: React.FC = () => {
    // Schemes and filters state
    const [allSchemes, setAllSchemes] = useState<SelectOption[]>([]);
    const [schemes, setSchemes] = useState<SelectOption[]>([]);
    const [pendingSchemes, setPendingSchemes] = useState<SelectOption[]>(DEFAULT_ALL);
    const [appliedSchemes, setAppliedSchemes] = useState<SelectOption[]>(DEFAULT_ALL);
    const [loadingSchemes, setLoadingSchemes] = useState<boolean>(true);

    const [investmentStrategies, setInvestmentStrategies] = useState<SelectOption[]>([]);
    const [selectedStrategies, setSelectedStrategies] = useState<SelectOption[]>(DEFAULT_ALL);
    const [pendingStrategies, setPendingStrategies] = useState<SelectOption[]>(DEFAULT_ALL);
    const [appliedStrategies, setAppliedStrategies] = useState<SelectOption[]>(DEFAULT_ALL);
    const [loadingStrategies, setLoadingStrategies] = useState<boolean>(true);

    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [indices, setIndices] = useState<SelectOption[]>([]);
    const [pendingBenchmarks, setPendingBenchmarks] = useState<SelectOption[]>(DEFAULT_BENCHMARK);
    const [appliedBenchmarks, setAppliedBenchmarks] = useState<SelectOption[]>(DEFAULT_BENCHMARK);
    const [loadingIndices, setLoadingIndices] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [pendingDate, setPendingDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loadingDate, setLoadingDate] = useState(true);
    const { accessCutoffDate, restrictionLoaded, isDateRestricted, clampDate } = useHoldingsDateRestriction();

    // When restriction loads after the date is already set, snap pending + applied dates
    useEffect(() => {
        if (isDateRestricted(pendingDate)) {
            setPendingDate(clampDate(pendingDate));
            setAppliedDate(clampDate(pendingDate));
        }
    }, [accessCutoffDate, pendingDate]);


    // Load investment strategies
    useEffect(() => {
        const loadInvestmentStrategies = async () => {
            try {
                setLoadingStrategies(true);
                const response = await supportingDataApi.getInvestmentStrategies({ params: { page_size: 1000, is_active: true } });
                const data = Array.isArray(response) ? response : [];
                const options: SelectOption[] = [
                    { value: 'all', label: 'All' },
                    ...data.map((is: any) => ({ value: is.code, label: `${is.code} - ${is.name}` }))
                ];
                const defaultData = data.find((is: any) => is.name === DEFAULT_STRATEGY_NAME);
                const defaultOption = defaultData ? options.find(opt => opt.value === defaultData.code) : null;
                if (!defaultOption) {
                    console.warn(`Default investment strategy '${DEFAULT_STRATEGY_NAME}' not found, falling back to All`);
                }
                const defaultSelection = defaultOption ? [defaultOption] : DEFAULT_ALL;
                setInvestmentStrategies(options);
                setSelectedStrategies(defaultSelection);
                setPendingStrategies(defaultSelection);
                setAppliedStrategies(defaultSelection);
            } catch (err: any) {
                console.error('Error loading investment strategies:', err);
                if (err.code === 'ERR_NETWORK') {
                    setError('Connection with Server Failed!');
                }
            } finally {
                setLoadingStrategies(false);
            }
        };

        loadInvestmentStrategies();
    }, []);

    // Load schemes
    useEffect(() => {
        const loadSchemes = async () => {
            try {
                setLoadingSchemes(true);
                const schemesData = await schemesApi.getSchemes({ page_size: 1000, is_active: true });

                // Store all schemes
                const allSchemeOptions: SelectOption[] = [
                    { value: 'all', label: 'All' },
                    ...schemesData.results.map((scheme) => ({
                        value: scheme.code || '',
                        label: scheme.code || '',
                    }))
                ];

                setAllSchemes(allSchemeOptions);
                setSchemes(allSchemeOptions);
                setPendingSchemes(DEFAULT_ALL);
                setAppliedSchemes(DEFAULT_ALL);
            } catch (err: any) {
                console.error('Error loading schemes:', err);
            } finally {
                setLoadingSchemes(false);
            }
        };

        loadSchemes();

        const loadIndices = async () => {
            try {
                setLoadingIndices(true);
                const indicesData = await sectorWeightAPI.getIndices();
                const allowedSymbols = ['BSEMID150', 'BSESML250', 'BSE500'];
                const indexOptions: SelectOption[] = indicesData
                    .filter((idx: any) => allowedSymbols.includes(idx.symbol))
                    .map((idx: any) => ({
                        value: idx.symbol,
                        label: idx.name
                    }));
                setIndices(indexOptions);
            } catch (err) {
                console.error('Error loading indices:', err);
            } finally {
                setLoadingIndices(false);
            }
        };
        loadIndices();
    }, []);

    // Called by LastWorkingDateFilter — first invocation is the auto-fetched default,
    // subsequent ones are user edits. We seed appliedDate only on the first call.
    const handleDateChange = (date: string) => {
        // Enforce max date — snap back to accessCutoffDate if user types a restricted date
        const effectiveDate = clampDate(date);
        setPendingDate(effectiveDate);
        if (loadingDate) {
            setAppliedDate(effectiveDate);
            setLoadingDate(false);
        }
    };

    const requestParams = useMemo(() => {
        const params: { scheme_codes?: string; benchmark?: string; investment_strategy_codes?: string; date?: string } = {};
        if (
            appliedSchemes.length > 0 &&
            !appliedSchemes.some((s) => s.value === 'all')
        ) {
            params.scheme_codes = appliedSchemes.map((s) => s.value).join(',');
        }
        if (appliedBenchmarks.length > 0) {
            params.benchmark = appliedBenchmarks.map((b) => b.value).join(',');
        }
        if (appliedStrategies.length > 0 && !appliedStrategies.some(strat => strat.value === 'all')) {
            params.investment_strategy_codes = appliedStrategies.map(strat => strat.value).join(',');
        }
        if (appliedDate) params.date = appliedDate;
        return params;
    }, [appliedSchemes, appliedBenchmarks, appliedStrategies, appliedDate]);


    const handleSchemeChange = (_event: any, value: SelectOption[]) => {
        if (!value || value.length === 0) {
            setPendingSchemes([]);
            return;
        }

        const hasAll = value.some(option => option.value === 'all');
        const lastSelected = value[value.length - 1];

        if (lastSelected.value === 'all') {
            setPendingSchemes(DEFAULT_ALL);
        } else if (hasAll && value.length > 1) {
            setPendingSchemes(value.filter(option => option.value !== 'all'));
        } else {
            setPendingSchemes(value);
        }
    };

    const handleStrategyChange = (_event: any, value: SelectOption[]) => {
        if (!value || value.length === 0) {
            setPendingStrategies([]);
            return;
        }

        const hasAll = value.some(option => option.value === 'all');
        const lastSelected = value[value.length - 1];

        if (lastSelected.value === 'all') {
            setPendingStrategies(DEFAULT_ALL);
        } else if (hasAll && value.length > 1) {
            setPendingStrategies(value.filter(option => option.value !== 'all'));
        } else {
            setPendingStrategies(value);
        }
    };

    const handleBenchmarkChange = (_event: any, value: SelectOption[]) => {
        setPendingBenchmarks(value);
    };


    const arraysEqualByValue = (a: SelectOption[], b: SelectOption[]) => {
        if (a.length !== b.length) return false;
        const aVals = a.map(x => x.value).sort();
        const bVals = b.map(x => x.value).sort();
        for (let i = 0; i < aVals.length; i++) {
            if (aVals[i] !== bVals[i]) return false;
        }
        return true;
    };

    const applyFilters = () => {
        if (isDateRestricted(pendingDate)) return;

        const schemesChanged = !arraysEqualByValue(pendingSchemes, appliedSchemes);
        const benchmarksChanged = !arraysEqualByValue(pendingBenchmarks, appliedBenchmarks);
        const strategiesChanged = !arraysEqualByValue(pendingStrategies, appliedStrategies);
        const dateChanged = pendingDate !== appliedDate;

        if (schemesChanged || benchmarksChanged || strategiesChanged || dateChanged) {
            setAppliedSchemes(pendingSchemes);
            setAppliedBenchmarks(pendingBenchmarks);
            setAppliedStrategies(pendingStrategies);
            setAppliedDate(pendingDate);
            setRefreshKey((prev) => prev + 1);
        }
    };

    const hasFilterChanges = !arraysEqualByValue(pendingSchemes, appliedSchemes) ||
        !arraysEqualByValue(pendingBenchmarks, appliedBenchmarks) ||
        !arraysEqualByValue(pendingStrategies, appliedStrategies) ||
        pendingDate !== appliedDate;

    return (
        <PageFrame
            header={
                <PageHeader
                    title="Sector Weight Analysis"
                    subtitle="Portfolio sector weights against the selected benchmark, as on the holding date."
                />
            }
        >
            <FilterBar
                hideSearch
                summary={appliedDate ? `Holdings as on ${appliedDate}` : 'Pick a holding date'}
                filters={
                    <>
                    <LastWorkingDateFilter
                        value={pendingDate}
                        onChange={handleDateChange}
                        label="Holding Date"
                        inputId="sector-holding-date"
                        max={accessCutoffDate ?? new Date().toISOString().split('T')[0]}
                    />

                    <FilterMultiSelect
                        label="Investment Strategies"
                        options={investmentStrategies}
                        value={pendingStrategies}
                        onChange={next => handleStrategyChange(null, next)}
                        loading={loadingStrategies}
                    />

                    <FilterMultiSelect
                        label="Schemes"
                        options={schemes}
                        value={pendingSchemes}
                        onChange={next => handleSchemeChange(null, next)}
                        loading={loadingSchemes}
                    />

                    <FilterMultiSelect
                        label="Benchmarks"
                        options={indices}
                        value={pendingBenchmarks}
                        onChange={next => handleBenchmarkChange(null, next)}
                        loading={loadingIndices}
                    />

                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title={!pendingDate ? 'A holding date is required' : hasFilterChanges ? 'Apply selected filters' : 'No changes to apply'}>
                            <span>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={applyFilters}
                                    disabled={!hasFilterChanges || !pendingDate}
                                >
                                    Apply Filters
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                    </>
                }
            />

            <SectorWeightComparison
                requestParams={requestParams}
                benchmarkOptions={indices}
                refreshKey={refreshKey}
                // Also wait for the date restriction (and its clamp of appliedDate) so the
                // first fetch isn't made with a too-recent date and then repeated.
                loadingFilters={loadingStrategies || loadingSchemes || loadingDate || !restrictionLoaded || isDateRestricted(appliedDate)}
            />
        </PageFrame>
    );
};

export default SectorWeightPage;